import React, { useCallback, useEffect, useState } from 'react';
import { injectIntl, WrappedComponentProps } from 'react-intl';
import JsFileDownloader, { OptionalParams } from 'js-file-downloader';
import { isHostnameContainsCC } from '../../../utils';
import { URL_PARTNUMBERS_DIVIDER, LOCAL_STORAGE_PARTNUMBER_DEVIDER } from '../../../config/consts';
import { ExportValues, messageSettings, ExportExtensions } from './consts';
import { ComparatorExcelHandler } from './comparator-excel-handler';
import { QdsSelect } from '@quartzds/core-react';

type Props = {
  cc: string;
  lc: string;
  isPublic: boolean;
  isApc?: boolean;
  partNumbersList: string;
  storeData?: any;
} & WrappedComponentProps;

const ExportTo: React.FC<Props> = ({cc, lc, partNumbersList, isApc, storeData, intl, isPublic }) => {
  const message = intl.formatMessage(messageSettings);
  const options = [
    { value: '', label: message},
    { value: ExportValues.pdf, label: `${message} ${ExportValues.pdf}` },
    { value: ExportValues.excel, label: `${message} ${ExportValues.excel}` }
  ];

  const [excelHandler, setExcelHandler] = useState(null);

  useEffect(() => {
    if(!excelHandler) {
      const excelHandlerInstance = new ComparatorExcelHandler(storeData, intl);
      setExcelHandler(excelHandlerInstance);
    } else {
      excelHandler.reset(storeData, intl);
    }
  }, [storeData, intl]);

  const onChange = useCallback(
    ({target: { value }}) => {
      if (value === '') return;

      const extension = value === ExportValues.pdf ? ExportExtensions.pdf : ExportExtensions.excel;
      const countryCode = isHostnameContainsCC() ? '' : `${cc}/`;
      let API_GET_UIASS_PRODUCTS = `${isApc ? '/apc/' : '/'}${countryCode}${lc}/product/api/products/comparison/export?product-ids=${partNumbersList}&format=${extension}`;

      const downloaderSettings = {
        url: API_GET_UIASS_PRODUCTS,
        forceDesktopMode: true,
        withCredentials: true,
        nameCallback: function () {
          return `Compare Products.${extension}`
        },
      };

      const optionalDownloaderSettings: OptionalParams = {};

      if(value === ExportValues.pdf) {
        const reversedPartNumberlist = partNumbersList.split(URL_PARTNUMBERS_DIVIDER).reverse();
        const commaSeparatedList = reversedPartNumberlist.join(LOCAL_STORAGE_PARTNUMBER_DEVIDER);
        const brandPath = isApc ? '/apc/' : '/se/';
        downloaderSettings.url = `${isPublic ? '/' : brandPath}${countryCode}${lc}/comparison/download-pdf?productIds=${commaSeparatedList}`;
        optionalDownloaderSettings.contentType = false;
        optionalDownloaderSettings.withCredentials = false;
      }

      if (value === ExportValues.excel) {
        excelHandler.exportToExcel();
        return;
      }

      new JsFileDownloader(Object.assign({}, downloaderSettings, optionalDownloaderSettings));
    },[excelHandler, cc, lc, partNumbersList, isApc, isPublic]
  );

  return (
    <QdsSelect
      onQdsChange={onChange}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
    </QdsSelect>
  );
};

export default injectIntl(ExportTo);
